import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

const formatNames = names =>
	[...(names ?? [])]
		.sort((a, b) => a.localeCompare(b))
		.map(name => name.replace(',', ', '));

const DetailItem = ({ label, children }) => (
	<Box>
		<Typography
			variant='caption'
			sx={{
				fontWeight: 'bold',
				color: 'var(--mdc-theme-primary)',
				textTransform: 'uppercase',
				letterSpacing: '0.05em',
			}}
		>
			{label}
		</Typography>
		<Box sx={{ mt: 0.25 }}>{children}</Box>
	</Box>
);

DetailItem.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};

const DetailText = ({ value }) => (
	<Typography variant='body2'>{value || 'No Data'}</Typography>
);

DetailText.propTypes = { value: PropTypes.string };

const NameList = ({ names }) => {
	const formatted = formatNames(names);
	return formatted.length ? (
		<Box component='ul' sx={{ m: 0, pl: 2 }}>
			{formatted.map(name => (
				<Typography key={name} component='li' variant='body2'>
					{name}
				</Typography>
			))}
		</Box>
	) : (
		<Typography variant='body2'>No Data</Typography>
	);
};

NameList.propTypes = { names: PropTypes.arrayOf(PropTypes.string) };

const ADGroupDetailsPanel = ({ result, open }) => (
	<Collapse in={open} unmountOnExit>
		<Box
			sx={{
				mt: 1,
				mb: 1,
				p: 2,
				border: '1px solid',
				borderColor: 'var(--mdc-theme-primary)',
				borderRadius: 1,
				backgroundColor: '#fafafa',
				display: 'grid',
				gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
				gap: 2,
			}}
		>
			<DetailItem label='Group Name'>
				<DetailText value={result.GroupName} />
			</DetailItem>

			<DetailItem label='Description'>
				<DetailText value={result.Description} />
			</DetailItem>

			<DetailItem label='Clinical Access'>
				<DetailText value={result.ClinicalAccess ? 'Yes' : 'No'} />
			</DetailItem>

			<Box sx={{ gridColumn: '1 / -1' }}>
				<Divider />
			</Box>

			<Box sx={{ gridColumn: '1 / -1' }}>
				<DetailItem label='LDAP Path'>
					<DetailText value={result.DistinguishedName} />
				</DetailItem>
			</Box>

			<Box sx={{ gridColumn: '1 / -1' }}>
				<Divider />
			</Box>

			<DetailItem label='Group Owners'>
				<NameList names={result.Owners} />
			</DetailItem>

			<DetailItem label='Approvers'>
				<NameList names={result.Approvers} />
			</DetailItem>
		</Box>
	</Collapse>
);

ADGroupDetailsPanel.propTypes = {
	result: PropTypes.shape({
		GroupName: PropTypes.string,
		Description: PropTypes.string,
		DistinguishedName: PropTypes.string,
		ClinicalAccess: PropTypes.bool,
		Owners: PropTypes.arrayOf(PropTypes.string),
		Approvers: PropTypes.arrayOf(PropTypes.string),
	}).isRequired,
	open: PropTypes.bool.isRequired,
};

export default ADGroupDetailsPanel;